import React, { useEffect, useRef } from "react";
import { createUseStyles } from "react-jss";
import { inject, observer } from "mobx-react";
import { useGetMobileTagsQuery } from "routes/operator/routes/hooks";
import { OPERATOR_TAB_CARD } from "stores/OperatorModel";
import Container from "reactstrap/lib/Container";
import Row from "reactstrap/lib/Row";
import { pipe } from "Utils/functionalUtils";
import { useQuery } from "@apollo/client";
import { MOBILE_TARIFFS_QUERY_2, MOBILE_OFFERED_TARIFFS } from "Services/queries";
import { DECLINATION_QUERY } from "Configs/queries";
import { LimitTariffsFromOperator } from "Constants/Limit";
import { useWithDefaultParams } from "Services/useWithDefaultParams";
import { sendMetriks } from "Services/sendMetriks";
import { isMainSiteCode } from "Utils/siteConfig";
import ReviewsSendHOC from "HOC/ReviewsSendHOC";
import { AddReviewContainerV2, ReviewListContainerV3 } from "Containers/Review";
import TagButtonComponent from "Containers/TagButtonComponent";
import { PaddingBottom, PaddingTop } from "ComponentsUI/Spacings";
import { CommonNoTariffsInfo, NoTariffsInfo } from "ComponentsUI/InfoBlocks";
import { ShowMoreButton } from "ComponentsUI/Buttons";
import { EmptyStubWrapper } from "ComponentsUI/EmptyStub";
import { StubOperatorTariffs } from "ComponentsUI/Stub";
import { AboutMe } from "ComponentsUI/Info";
import { ShouldAddConvergentProvider } from "providers/shouldAddConvergentProvider";
import { BlockReviews } from "ComponentsUI/Blocks";
import MobileTariffList from "ComponentsUI/MobileTariffList";
import OperatorContacts from "./Containers/OperatorContacts";

const useStyles = createUseStyles((theme) => ({
	[`@media (min-width: ${theme.media.md})`]: {
		container: {
			marginTop: "50px",
		},
	},
	noFindTariffsContainer: {
		margin: "0 0 50px 0",
	},
}));

const AboutOperator = (props) => {
	let {
		ApplicationModel: {
			windowSizeModel: { windowSizes },
			tariffsModel,
			text,
		},
		seoData: { aboutWETitle = "", aboutWEText = "" } = {},
		operatorForCard,
	} = props;
	let tariffListRef = useRef(null);
	let match = useWithDefaultParams();
	let regionUrl = match?.params?.regionUrl || "";
	const siteCode = props.ApplicationModel.siteCode;

	const {
		info: { cnt_tariffs = 0 },
		global: { id: globalId },
	} = operatorForCard;

	useEffect(() => {
		const {
			ApplicationModel: { operatorModel },
		} = props;
		if (operatorModel.visibleTab !== OPERATOR_TAB_CARD) {
			operatorModel.changeTab(OPERATOR_TAB_CARD);
		}
	}, []);

	const classes = useStyles();

	const { isLoading: isMobileTagsLoading, mobileTags } = useGetMobileTagsQuery(!cnt_tariffs);

	// Создание фильтра для списка тарифов
	const filter = [];
	if (regionUrl) {
		filter.push(`region.url=${regionUrl}`);
	}

	if (match.params.operator) {
		filter.push(`provider.url_name=${match.params.operator}`);
	}

	// Запрос в БД на список тарифов по выбранному фильтру.
	const tariffsQuery = useQuery(MOBILE_TARIFFS_QUERY_2, {
		skip: !cnt_tariffs,
		variables: {
			filter: filter.join("&"),
			limit: LimitTariffsFromOperator,
			offset: 0,
			providerJoin: true,
			sort: "-is_exclusive",
		},
		notifyOnNetworkStatusChange: true,
	});

	// Запрос в БД на предложенные тарифы
	const offeredTariffsQuery = useQuery(MOBILE_OFFERED_TARIFFS, {
		skip: !!cnt_tariffs,
		variables: {
			region_url: regionUrl,
			limitProvider: 1,
			providerJoin: true,
		},
	});

	// Запрос на склонение регионов
	const regionDeclinationQuery = useQuery(DECLINATION_QUERY, {
		skip: !!cnt_tariffs,
		variables: {
			filter: `url=${regionUrl}`,
		},
	});

	useEffect(() => {
		if (tariffListRef?.current) {
			tariffsModel.setTariffListRef(tariffListRef.current);
		}
		return () => {
			if (tariffListRef?.current) {
				tariffsModel.setTariffListRef(null);
			}
		};
	}, [tariffListRef.current]);

	useEffect(() => {
		if (!!offeredTariffsQuery?.data?.tariffsOffered?.data?.length) {
			sendMetriks("OPEN_RECENTLY_CONNECTED_OPERATORY");
		}
	}, [offeredTariffsQuery.loading]);

	// Условия показать экран загрузки
	if (
		isMobileTagsLoading ||
		tariffsQuery.loading ||
		offeredTariffsQuery.loading ||
		regionDeclinationQuery.loading
	) {
		return <StubOperatorTariffs />;
	}

	// Срохранение в переменную один из доступных тарифов
	let selectedTariff = cnt_tariffs
		? tariffsQuery?.data?.mobileTariffs?.data
		: offeredTariffsQuery?.data?.tariffsOffered?.data;

	// Если тарифы не загрузились, то показать плашку
	if (!selectedTariff) {
		return (
			<EmptyStubWrapper
				headerText={text.NO_TARIFFS_HEADER}
				displayText={<NoTariffsInfo text={text} ratesMobile={true} />}
				buttonText={text.TO_MAIN_PAGE}
				iconHeader={"icon-balance"}
				iconButton={"icon-playlist-add"}
			/>
		);
	}

	// Загрузка дополнительного списка тарифов
	const fetchMoreTariffs = () => {
		tariffsQuery.fetchMore({
			variables: {
				offset: tariffsQuery.data.mobileTariffs.data.length,
			},
			updateQuery: (prev, { fetchMoreResult }) => {
				if (!fetchMoreResult) return prev;

				return Object.assign({}, prev, {
					mobileTariffs: {
						...prev.mobileTariffs,
						data: [...prev.mobileTariffs.data, ...fetchMoreResult.mobileTariffs.data],
					},
				});
			},
		});
	};

	return (
		<PaddingTop containerClass={classes.container}>
			<PaddingBottom>
				<Container>
					{isMainSiteCode(siteCode) && <div></div>}
					{!tariffsQuery?.data?.mobileTariffs?.data?.length ? (
						<Row>
							<div className={classes.noFindTariffsContainer}>
								<CommonNoTariffsInfo text={text.NO_FIND_OPERATOR_TARIFFS} />
							</div>
						</Row>
					) : null}
					<TagButtonComponent
						mobileTags={mobileTags}
						tagCode={match.params.tagCode}
						operatorRates={true}
						operatorStyle={true}
					/>
					{selectedTariff && (
						<Row>
							<ShouldAddConvergentProvider value={true}>
								<MobileTariffList
									ref={tariffListRef}
									tariffs={selectedTariff}
									thisOfferedTariff={!cnt_tariffs}
									declinationsName={regionDeclinationQuery.data?.region.declinations.prepositional}
									text={text}
								/>
							</ShouldAddConvergentProvider>
						</Row>
					)}
					{tariffsQuery?.data?.mobileTariffs?.data?.length <
						tariffsQuery?.data?.mobileTariffs?.pagination?.items_total && (
						<ShowMoreButton
							disabled={tariffsQuery.loading}
							windowSizes={windowSizes}
							text={text}
							onClickHandler={fetchMoreTariffs}
						/>
					)}
					<PaddingTop>
						<BlockReviews
							reviewLocation="about_operator"
							queryType="regionFull"
							text={text}
							reviews={
								<ReviewListContainerV3
									text={text}
									reviewLocation="about_operator"
									queryType="reviews"
								/>
							}
							control={
								<ReviewsSendHOC>
									<AddReviewContainerV2
										reviewLocation="about_operator"
										queryType="operator"
										selectProvidersPlaceholder={text.filtersOperator}
										textHeader={text.WRITE_REVIEW}
									/>
								</ReviewsSendHOC>
							}
							datatest={"reviews_about_operator"}
						/>
					</PaddingTop>
					{!!aboutWEText && (
						<AboutMe title={aboutWETitle} data={aboutWEText} windowSizes={windowSizes} />
					)}
					<PaddingTop>
						<OperatorContacts operatorForCard={operatorForCard} />
					</PaddingTop>
				</Container>
			</PaddingBottom>
		</PaddingTop>
	);
};

export default pipe(observer, inject("ApplicationModel"))(AboutOperator);
